import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useStreamUnitLocation } from '../../common/hooks/use-stream-unit-location';
import { isValidLatLng } from '../../location/helpers';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { GOOGLE_MAPS_BASE_URL } from './constants';
import { FrEventFormType } from '../../fr-entity-panels/types';
import { getAddressForUrl } from './helpers';
import { useUnitLocationStore } from '../../fr-unit-location/use-unit-location-store';

export function useGoogleMapsRouteUrl() {
    const unitId = useUnitAssignment()?.unitId;
    const { control } = useFormContext<FrEventFormType>();
    const { lat: unitGpsLatitude, lng: unitGpsLongitude } =
        useUnitLocationStore()?.unitGpsInfo || {};
    const unitGeoLocation = useStreamUnitLocation({ unitId });
    const eventLocation = useWatch({ control, name: 'location' });

    return useMemo(() => {
        const unitHasValidGpsLocation = isValidLatLng(unitGpsLatitude, unitGpsLongitude);
        const unitHasValidLocation =
            unitHasValidGpsLocation || !!unitGeoLocation?.consolidatedLocationView;

        if (!eventLocation || !unitHasValidLocation) {
            return null;
        }

        const {
            displayLineFirst: unitAddressDisplayLineFirst,
            displayLineLast: unitAddressDisplayLineLast,
            latitude: unitGeoLatitude,
            longitude: unitGeoLongitude,
        } = unitGeoLocation?.consolidatedLocationView ?? {};
        const {
            displayLineFirst: eventAddressDisplayLineFirst,
            displayLineLast: eventAddressDisplayLineLast,
            latitude: eventLatitude,
            longitude: eventLongitude,
        } = eventLocation ?? {};

        const eventCoordinatesForUrl = isValidLatLng(eventLatitude, eventLongitude)
            ? `${eventLatitude},${eventLongitude}`
            : null;
        let unitCoordinatesForUrl = unitHasValidGpsLocation
            ? `${unitGpsLatitude},${unitGpsLongitude}`
            : null;

        if (!unitHasValidGpsLocation && isValidLatLng(unitGeoLatitude, unitGeoLongitude)) {
            unitCoordinatesForUrl = `${unitGeoLatitude},${unitGeoLongitude}`;
        }

        const routeOriginUrlPath =
            unitCoordinatesForUrl ||
            getAddressForUrl(unitAddressDisplayLineFirst, unitAddressDisplayLineLast) ||
            '';
        const routeDestinationUrlPath =
            eventCoordinatesForUrl ||
            getAddressForUrl(eventAddressDisplayLineFirst, eventAddressDisplayLineLast) ||
            '';

        return `${GOOGLE_MAPS_BASE_URL}${routeOriginUrlPath}/${routeDestinationUrlPath}`;
    }, [
        eventLocation,
        unitGeoLocation?.consolidatedLocationView,
        unitGpsLatitude,
        unitGpsLongitude,
    ]);
}
